/**
 * Router Utils
 */

import {
  Location,
  LocationDescriptorObject,
  Match,
  ObjectStringMap,
  RenderProps,
  Router,
  RouteRenderArgs,
} from 'found';
import { identity, isEmpty, omit, pickBy } from 'lodash';
import * as qs from 'query-string';
import * as React from 'react';
import { Store } from 'redux';

import LoadingSpinner from '../components/atoms/feedback/LoadingSpinner';
import PageHeader, { PageHeaderProps } from '../components/molecules/blocks/PageHeader';
import Page from '../containers/Page';
import { getEncodedQuery } from '../contexts/queryString/helpers';
import { LicenseFilter } from '../contexts/queryString/license';
import { QueryStringParam } from '../contexts/queryString/types';

interface ILoadableRouteProps<P = {}> {
  extraProps?: P;
  pageHeaderProps?: PageHeaderProps;
}

export interface IMatchWithContext extends Match {
  context: {
    store: Store<IStoreState>;
  } | undefined;
}

type RenderedComponentProps<P> = P & RenderProps;
export interface IRenderArgsWithProps<P> extends RouteRenderArgs {
  Component?: React.ComponentType<P & Optional<RenderProps>>;
  props?: RenderedComponentProps<P>;
}

export type PrepareVariablesFunction<Params, Variables> = (...args: [Params, IMatchWithContext]) => NonNull<Variables>;
export type RenderRouteFunction<Props> = (args: IRenderArgsWithProps<Props>) => React.ReactElement<Props>;

const defaultList = [];
const QueryStringPersistenceBlackList = ['goto', 'view'];

export const getStateFromMatchContext =
  (match: IMatchWithContext) => match.context ? match.context.store.getState() : undefined;

export function getRouteLocation(pathname: string, query: ObjectStringMap) {
  const location: LocationDescriptorObject = { pathname };

  if (!isEmpty(query)) {
    const cleanQuery = pickBy(query, identity) as ObjectStringMap;
    location.search = `?${qs.stringify(getEncodedQuery(cleanQuery), { encode: false })}`;
  } else {
    location.query = {};
  }

  return location;
}

export function refreshRoute(location: Location, router: Router, query: ObjectStringMap = location.query) {
  router.replace(getRouteLocation(location.pathname, query));
}

export function goto(pathname: string, query: ObjectStringMap, router: Router) {
  const location = getRouteLocation(pathname, query);
  router.push(location);
}

export function getOriginLocation() {
  return window.location.origin ||
    `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;
}

export function createHref(location: LocationDescriptorObject, withOrigin?: boolean) {
  return `${withOrigin ? getOriginLocation() : ''}${location.pathname}${location.search || ''}${location.hash || ''}`;
}

export function linkWithQueryString(pathname: string, query: ObjectStringMap) {
  return createHref(getRouteLocation(pathname, query));
}

export function linkWithCurrentQueryString(pathname: string) {
  const query = qs.parse(window.location.search);
  const sanitizedQuery = omit(query, QueryStringPersistenceBlackList) as ObjectStringMap;

  return linkWithQueryString(pathname, sanitizedQuery);
}

export function LoadingPage<P>({ routeProps }: { routeProps?: ILoadableRouteProps<P> }) {
  return (
    <Page id="loading" layout="loose">
      {routeProps && routeProps.pageHeaderProps && <PageHeader {...routeProps.pageHeaderProps} />}
      <LoadingSpinner center fixed />
    </Page>
  );
}

export function renderLoadableRoute<P>(routeProps?: ILoadableRouteProps<P>) {
  return ({ Component, props }: IRenderArgsWithProps<P>) => {
    if (!Component || !props) {
      return <LoadingPage routeProps={routeProps} />;
    }
    return <Component {...props} {...(routeProps && routeProps.extraProps)} />;
  };
}

export function getLicenseParamsForRouteQuery(match: IMatchWithContext) {
  const query = match.location.query;
  return query && QueryStringParam.License in query ?
    LicenseFilter.unserialize(query)
    : defaultList;
}

export function getSimilaritySeedIdForRouteQuery(match: IMatchWithContext) {
  const query = match.location.query;
  return query && QueryStringParam.Similarity in query ?
    query[QueryStringParam.Similarity]
    : undefined;
}

export function prepareVariablesForDiscoverPage(_, match: IMatchWithContext) {
  return {
    license_params: getLicenseParamsForRouteQuery(match),
  };
}

export function prepareVariablesForCollectionPage(params, match: IMatchWithContext) {
  return {
    query: '',
    filters: {
      collection_slug: params.slug,
    },
    license_params: getLicenseParamsForRouteQuery(match),
  };
}

export function prepareVariablesForProjectsPage({ id }) {
  return {
    id: id || '',
    isDefaultRoute: !!!id,
  };
}

export function prepareVariablesForOrdersPage({ invoice_number }) {
  const variables: any = {
    isDefaultRoute: !!!invoice_number,
  };

  if (invoice_number) {
    variables.invoice_number = invoice_number;
  }

  return variables;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/utils/router.tsx